import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || '/api');

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const CONTENT_TYPES = [
  { key: 'live', label: '📺 Chaînes' },
  { key: 'vod', label: '🎬 VOD' },
  { key: 'series', label: '📁 Séries' }
];

const isIgettvCategory = (cat) => {
  // Pour le live, la source BDD est la vérité: relay vs provider/iGetTV/DXServer.
  // Pour VOD/Séries, le backend annote maintenant iGetTV via source.
  return cat?.source === 'provider'
    && !String(cat?.category_id || '').startsWith('dx_vod_');
};

const isDxserverCategory = (cat) => (
  cat?.source === 'dxserver'
  || String(cat?.category_id || '').startsWith('dx_vod_')
);

const isPremierServicesCategory = (cat) => cat?.source === 'premier_services';

const isDndnsCategory = (cat) => cat?.source === 'dndns';

const normalizeSearch = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const compareCategoryOrder = (a, b) => {
  const orderA = Number(a.sort_order) || 0;
  const orderB = Number(b.sort_order) || 0;
  const hasOrderA = orderA > 0;
  const hasOrderB = orderB > 0;

  if (hasOrderA !== hasOrderB) return hasOrderA ? -1 : 1;
  if (hasOrderA && orderA !== orderB) return orderA - orderB;

  return (a.category_name || '').localeCompare(b.category_name || '')
    || String(a.source || '').localeCompare(String(b.source || ''))
    || String(a.category_id || '').localeCompare(String(b.category_id || ''));
};

// ==================== COMPOSANT PANEL ====================
function CategoryPanel({ title, icon, cats, color, blockedKeys, onToggle, onBulk, saving, search, mode, onReorder, pendingOrder }) {
  const searchTerm = normalizeSearch(search.trim());
  const baseList = mode === 'reorder' ? (pendingOrder || cats) : cats;
  const filtered = searchTerm
    ? baseList.filter(c => (
        normalizeSearch(c.category_name).includes(searchTerm)
        || normalizeSearch(c.category_id).includes(searchTerm)
        || normalizeSearch(c.source).includes(searchTerm)
      ))
    : baseList;

  const displayList = filtered;
  const blocked = filtered.filter(c => blockedKeys.has(`${c.category_type}:${c.category_id}`));
  const visible = filtered.filter(c => !blockedKeys.has(`${c.category_type}:${c.category_id}`));

  const [selected, setSelected] = useState(new Set());
  const [dragOver, setDragOver] = useState(null);
  const dragSrcIdx = useRef(null);
  const lastClickIdx = useRef(null);

  useEffect(() => {
    setSelected(new Set());
    lastClickIdx.current = null;
  }, [mode, search]);

  const catKey = (cat) => `${cat.category_type}:${cat.source || 'provider'}:${cat.category_id}`;

  const reorderSelectedAround = (targetKey, insertAfter = false) => {
    const selectedKeys = new Set([...selected]);
    const selectedItems = baseList.filter(c => selectedKeys.has(catKey(c)));
    if (!selectedItems.length) return;

    const rest = baseList.filter(c => !selectedKeys.has(catKey(c)));
    let insertIdx = rest.length;
    if (targetKey) {
      insertIdx = rest.findIndex(c => catKey(c) === targetKey);
      if (insertIdx === -1) insertIdx = rest.length;
      if (insertAfter) insertIdx += 1;
    }

    rest.splice(Math.min(Math.max(insertIdx, 0), rest.length), 0, ...selectedItems);
    onReorder(rest);
  };

  const toggleSelect = (e, i, key) => {
    if (mode !== 'reorder') return;
    e.stopPropagation();
    if (e.shiftKey && lastClickIdx.current !== null) {
      const from = Math.min(lastClickIdx.current, i);
      const to = Math.max(lastClickIdx.current, i);
      setSelected(prev => {
        const n = new Set(prev);
        for (let j = from; j <= to; j++) n.add(catKey(displayList[j]));
        return n;
      });
    } else if (e.ctrlKey || e.metaKey) {
      setSelected(prev => {
        const n = new Set(prev);
        n.has(key) ? n.delete(key) : n.add(key);
        return n;
      });
    } else {
      // Simple click = toggle (add if not selected, remove if selected)
      setSelected(prev => {
        const n = new Set(prev);
        n.has(key) ? n.delete(key) : n.add(key);
        return n;
      });
    }
    lastClickIdx.current = i;
  };

  const selectAll = () => setSelected(new Set(displayList.map(catKey)));
  const clearSelect = () => setSelected(new Set());

  const handleDragStart = (e, i) => {
    dragSrcIdx.current = i;
    const key = catKey(displayList[i]);
    if (!selected.has(key)) {
      setSelected(new Set([key]));
      lastClickIdx.current = i;
    }
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, i) => { e.preventDefault(); setDragOver(i); };

  const handleDrop = (e, i) => {
    e.preventDefault();
    setDragOver(null);
    if (dragSrcIdx.current === null) return;
    if (!displayList[i]) return;

    const dropKey = catKey(displayList[i]);
    if (selected.has(dropKey)) {
      dragSrcIdx.current = null;
      return;
    }

    const selectedDisplayIdxs = displayList
      .map((c, idx) => selected.has(catKey(c)) ? idx : -1)
      .filter(idx => idx >= 0);
    const firstSelOrigIdx = selectedDisplayIdxs[0] ?? dragSrcIdx.current;
    reorderSelectedAround(dropKey, i > firstSelOrigIdx);
    dragSrcIdx.current = null;
  };

  const handleDragEnd = () => { dragSrcIdx.current = null; setDragOver(null); };

  const moveSelected = (dir) => {
    if (!selected.size) return;
    const selIdxs = displayList.map((c, i) => selected.has(catKey(c)) ? i : -1).filter(i => i >= 0);
    if (!selIdxs.length) return;
    if (dir === 'up' && selIdxs[0] === 0) return;
    if (dir === 'down' && selIdxs[selIdxs.length - 1] === displayList.length - 1) return;

    if (dir === 'up') {
      reorderSelectedAround(catKey(displayList[selIdxs[0] - 1]), false);
    } else {
      reorderSelectedAround(catKey(displayList[selIdxs[selIdxs.length - 1] + 1]), true);
    }
  };

  return (
    <div style={{ flex: 1, background: '#1a1a2e', borderRadius: 12, border: `1px solid ${color}33`, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', background: `${color}15`, borderBottom: `1px solid ${color}33` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: 15, fontWeight: 700, color }}>{icon} {title}</span>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
              {mode === 'block'
                ? <><span style={{ color: '#ef4444' }}>{blocked.length} bloquées</span> · <span style={{ color: '#22c55e' }}>{visible.length} visibles</span> · <span style={{ color: '#888' }}>{filtered.length} total</span></>
                : <span style={{ color: '#f59e0b' }}>🔀 {displayList.length} catégories{selected.size > 0 ? <> · <strong style={{ color }}>{selected.size} sélectionnées</strong></> : ''}</span>
              }
            </div>
          </div>

          {mode === 'block' ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => onBulk(visible, 'block')} disabled={saving || !visible.length}
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #ef4444', background: '#ef444420', color: '#ef4444', cursor: 'pointer', fontSize: 11, fontWeight: 600, opacity: !visible.length ? 0.4 : 1 }}>
                🚫 Tout bloquer
              </button>
              <button onClick={() => onBulk(blocked, 'unblock')} disabled={saving || !blocked.length}
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #22c55e', background: '#22c55e20', color: '#22c55e', cursor: 'pointer', fontSize: 11, fontWeight: 600, opacity: !blocked.length ? 0.4 : 1 }}>
                ✅ Tout débloquer
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <button onClick={selectAll} style={{ padding: '4px 9px', borderRadius: 5, border: `1px solid ${color}`, background: `${color}20`, color, cursor: 'pointer', fontSize: 11 }}>☑ Tout</button>
              <button onClick={clearSelect} disabled={!selected.size}
                style={{ padding: '4px 9px', borderRadius: 5, border: '1px solid #555', background: '#2a2a3e', color: '#aaa', cursor: selected.size ? 'pointer' : 'not-allowed', fontSize: 11, opacity: selected.size ? 1 : 0.4 }}>
                ✕ Désel.
              </button>
              <button onClick={() => moveSelected('up')} disabled={!selected.size} title="Monter"
                style={{ padding: '4px 8px', borderRadius: 5, border: '1px solid #555', background: '#2a2a3e', color: '#aaa', cursor: selected.size ? 'pointer' : 'not-allowed', fontSize: 13, opacity: selected.size ? 1 : 0.4 }}>▲</button>
              <button onClick={() => moveSelected('down')} disabled={!selected.size} title="Descendre"
                style={{ padding: '4px 8px', borderRadius: 5, border: '1px solid #555', background: '#2a2a3e', color: '#aaa', cursor: selected.size ? 'pointer' : 'not-allowed', fontSize: 13, opacity: selected.size ? 1 : 0.4 }}>▼</button>
            </div>
          )}
        </div>
        {mode === 'reorder' && (
          <div style={{ fontSize: 10, color: '#444', marginTop: 5 }}>
            💡 Click = 1 · Shift+Click = plage · Ctrl+Click = multi · Glisser = déplacer · ▲▼ = déplacer la sélection
          </div>
        )}
      </div>

      {/* Liste */}
      <div style={{ maxHeight: 520, overflowY: 'auto', padding: '4px 0' }}>
        {displayList.length === 0
          ? <div style={{ padding: 40, textAlign: 'center', color: '#555', fontSize: 14 }}>Aucune catégorie</div>
          : displayList.map((cat, i) => {
              const key = catKey(cat);
              // blockedKeys uses "type:id" format (no source), catKey uses "type:source:id"
              const blockKey = `${cat.category_type}:${cat.category_id}`;
              const isBlocked = blockedKeys.has(blockKey);
              const isSel = selected.has(key);
              const isDragOver = dragOver === i && mode === 'reorder';
              return (
                <div key={key}
                  draggable={mode === 'reorder'}
                  onClick={(e) => toggleSelect(e, i, key)}
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDrop={(e) => handleDrop(e, i)}
                  onDragEnd={handleDragEnd}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 14px', userSelect: 'none',
                    borderLeft: mode === 'reorder' ? (isSel ? `3px solid ${color}` : '3px solid transparent') : (isBlocked ? '3px solid #ef4444' : '3px solid transparent'),
                    background: isSel && mode === 'reorder' ? `${color}18` : isDragOver ? `${color}30` : (isBlocked && mode === 'block') ? '#ef444408' : 'transparent',
                    cursor: mode === 'reorder' ? 'pointer' : 'default',
                    borderBottom: isDragOver ? `2px solid ${color}` : '2px solid transparent',
                    transition: 'background 0.08s',
                  }}
                >
                  {mode === 'reorder' && (
                    <span style={{ color: isSel ? color : '#444', marginRight: 8, fontSize: 13, flexShrink: 0 }}>
                      {isSel ? '☑' : '☐'}
                    </span>
                  )}
                  {mode === 'reorder' && (
                    <span style={{ color: '#444', marginRight: 8, fontSize: 13, flexShrink: 0, cursor: 'grab' }}>⠿</span>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: (isBlocked && mode === 'block') ? '#666' : isSel ? '#fff' : '#ddd', fontWeight: isSel ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: (isBlocked && mode === 'block') ? 'line-through' : 'none' }}>
                      {cat.category_name || '(Sans nom)'}
                    </div>
                    <div style={{ fontSize: 11, color: '#555', marginTop: 1 }}>
                      ID: {cat.category_id} · {cat.count ?? 0} contenus
                      {mode === 'reorder' && cat.sort_order > 0 && <span style={{ color, marginLeft: 6 }}>#{cat.sort_order}</span>}
                    </div>
                  </div>
                  {mode === 'block' && (
                    <button onClick={(e) => { e.stopPropagation(); onToggle(cat); }} disabled={saving}
                      style={{ marginLeft: 10, padding: '4px 10px', borderRadius: 6, border: `1px solid ${isBlocked ? '#ef4444' : '#22c55e'}`, background: isBlocked ? '#ef444420' : '#22c55e20', color: isBlocked ? '#ef4444' : '#22c55e', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', opacity: saving ? 0.6 : 1, flexShrink: 0 }}>
                      {isBlocked ? '🚫 Bloqué' : '✅ Visible'}
                    </button>
                  )}
                </div>
              );
            })
        }
      </div>
    </div>
  );
}

// ==================== PAGE PRINCIPALE ====================
export default function AdminCategoryBlock() {
  const [allCategories, setAllCategories] = useState([]);
  const [blockedKeys, setBlockedKeys] = useState(new Set());
  const [activeType, setActiveType] = useState('live');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mode, setMode] = useState('block');
  const [pendingRelay, setPendingRelay] = useState(null);
  const [pendingXtream, setPendingXtream] = useState(null);
  const [pendingDxserver, setPendingDxserver] = useState(null);
  const [pendingGlobal, setPendingGlobal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [catsRes, blockedRes] = await Promise.all([
        fetch(`${API_URL}/admin/channels-manager/categories`, { headers: authHeaders() }),
        fetch(`${API_URL}/admin/channels-manager/global-blocked-categories`, { headers: authHeaders() })
      ]);
      const catsData = await catsRes.json();
      const blockedData = await blockedRes.json();
      setAllCategories(catsData.categories || []);
      setBlockedKeys(new Set((blockedData.blocked || []).map(b => `${b.category_type}:${b.category_id}`)));
    } catch { setError('Erreur lors du chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setPendingRelay(null);
    setPendingXtream(null);
    setPendingDxserver(null);
    setPendingGlobal(null);
  }, [activeType, mode]);

  const showMsg = (msg, isErr = false) => {
    if (isErr) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 3000);
  };

  const byType = useMemo(() => allCategories.filter(c => c.category_type === activeType), [allCategories, activeType]);
  const globalBase = useMemo(() => [...byType].sort(compareCategoryOrder), [byType]);
  const relayBase  = useMemo(() => byType.filter(c => c.source === 'relay').sort(compareCategoryOrder), [byType]);
  const igettBase  = useMemo(() => byType.filter(c => isIgettvCategory(c)).sort(compareCategoryOrder), [byType]);
  const dxserverBase = useMemo(() => byType.filter(c => isDxserverCategory(c)).sort(compareCategoryOrder), [byType]);
  const premierServicesBase = useMemo(() => byType.filter(isPremierServicesCategory).sort(compareCategoryOrder), [byType]);
  const dndnsBase = useMemo(() => byType.filter(isDndnsCategory).sort(compareCategoryOrder), [byType]);
  const activeVisibleOnly = useCallback((items) => (
    items.filter(c => (c.count ?? 0) > 0 && !blockedKeys.has(`${c.category_type}:${c.category_id}`))
  ), [blockedKeys]);
  const relayReorderBase = useMemo(() => activeVisibleOnly(relayBase), [relayBase, activeVisibleOnly]);
  const igettReorderBase = useMemo(() => activeVisibleOnly(igettBase), [igettBase, activeVisibleOnly]);
  const dxserverReorderBase = useMemo(() => activeVisibleOnly(dxserverBase), [dxserverBase, activeVisibleOnly]);
  const globalReorderBase = useMemo(() => activeVisibleOnly(globalBase), [globalBase, activeVisibleOnly]);

  const toggle = async (cat) => {
    const key = `${cat.category_type}:${cat.category_id}`;
    const isBlocked = blockedKeys.has(key);
    setSaving(true);
    try {
      if (isBlocked) {
        await fetch(`${API_URL}/admin/channels-manager/global-blocked-categories/${cat.category_id}?category_type=${cat.category_type}`, { method: 'DELETE', headers: authHeaders() });
        setBlockedKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
      } else {
        await fetch(`${API_URL}/admin/channels-manager/global-blocked-categories`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ category_id: cat.category_id, category_type: cat.category_type }) });
        setBlockedKeys(prev => new Set([...prev, key]));
      }
    } catch { showMsg('Erreur de mise à jour', true); }
    finally { setSaving(false); }
  };

  const bulkAction = async (cats, action) => {
    if (!cats.length) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/channels-manager/global-blocked-categories/bulk`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ action, categories: cats.map(c => ({ category_id: c.category_id, category_type: c.category_type })) }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setBlockedKeys(prev => {
        const n = new Set(prev);
        cats.forEach(c => { const k = `${c.category_type}:${c.category_id}`; action === 'block' ? n.add(k) : n.delete(k); });
        return n;
      });
      showMsg(`${data.count} catégories ${action === 'block' ? 'bloquées' : 'débloquées'}`);
    } catch { showMsg("Erreur lors de l'action groupée", true); }
    finally { setSaving(false); }
  };

  const saveOrder = async () => {
    // Un seul panneau unifié pour tous les types en mode réorganisation
    const allOrdered = pendingGlobal || globalReorderBase;
    const updates = allOrdered.map((cat, i) => ({ category_id: cat.category_id, category_type: cat.category_type, source: cat.source, sort_order: i + 1 }));
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/channels-manager/categories/reorder`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ updates, order_scope: 'global' })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setAllCategories(prev => prev.map(cat => {
        const u = updates.find(u =>
          String(u.category_id) === String(cat.category_id)
          && u.category_type === cat.category_type
          && (cat.category_type !== 'live' || u.source === cat.source)
        );
        return u ? { ...cat, sort_order: u.sort_order } : cat;
      }));
      setPendingRelay(null); setPendingXtream(null); setPendingDxserver(null); setPendingGlobal(null);
      showMsg(`Ordre sauvegardé — ${data.count} catégories`);
    } catch { showMsg('Erreur lors de la sauvegarde', true); }
    finally { setSaving(false); }
  };

  const hasPendingChanges = pendingGlobal !== null;
  const totalBlocked = [...blockedKeys].filter(k => k.startsWith(`${activeType}:`)).length;

  return (
    <AdminLayout>
      <div style={{ padding: '24px', color: '#e0e0e0', minHeight: '100vh', background: '#0f0f1a' }}>
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#fff' }}>🚧 Gestion des Catégories</h1>
          <p style={{ color: '#888', marginTop: 4, fontSize: 13 }}>Bloquez des catégories pour tous les utilisateurs ou réorganisez l'ordre d'affichage.</p>
        </div>

        {error   && <div style={{ padding: '9px 14px', borderRadius: 8, background: '#ef444420', border: '1px solid #ef4444', color: '#ef4444', marginBottom: 14, fontSize: 13 }}>{error}</div>}
        {success && <div style={{ padding: '9px 14px', borderRadius: 8, background: '#22c55e20', border: '1px solid #22c55e', color: '#22c55e', marginBottom: 14, fontSize: 13 }}>{success}</div>}

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {CONTENT_TYPES.map(ct => (
            <button key={ct.key} onClick={() => { setActiveType(ct.key); setSearch(''); }}
              style={{ padding: '9px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: activeType === ct.key ? '#6366f1' : '#1a1a2e', color: activeType === ct.key ? '#fff' : '#888' }}>
              {ct.label}
            </button>
          ))}
          <div style={{ width: 1, height: 32, background: '#333', margin: '0 4px' }} />
          <button onClick={() => setMode(mode === 'block' ? 'reorder' : 'block')}
            style={{ padding: '9px 18px', borderRadius: 8, border: `1px solid ${mode === 'reorder' ? '#f59e0b' : '#444'}`, cursor: 'pointer', fontSize: 13, fontWeight: 600, background: mode === 'reorder' ? '#f59e0b20' : '#1a1a2e', color: mode === 'reorder' ? '#f59e0b' : '#aaa' }}>
            {mode === 'reorder' ? '✅ Mode blocage' : '🔀 Réorganiser'}
          </button>
          {mode === 'reorder' && (
            <button onClick={saveOrder} disabled={saving || !hasPendingChanges}
              style={{ padding: '9px 18px', borderRadius: 8, border: 'none', cursor: hasPendingChanges && !saving ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700, background: hasPendingChanges ? '#6366f1' : '#2a2a3e', color: hasPendingChanges ? '#fff' : '#555' }}>
              {saving ? '⏳ Sauvegarde...' : "💾 Sauvegarder l'ordre"}
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
            {mode === 'block' && <span style={{ fontSize: 12, color: '#ef4444' }}>🚫 {totalBlocked} bloquées</span>}
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher..."
              style={{ padding: '8px 14px', borderRadius: 8, background: '#1a1a2e', border: '1px solid #333', color: '#e0e0e0', fontSize: 13, outline: 'none', width: 200 }} />
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: '#888' }}>⏳ Chargement...</div>
        ) : mode === 'reorder' ? (
          <CategoryPanel title={`Ordre global — ${CONTENT_TYPES.find(ct => ct.key === activeType)?.label || activeType}`} icon="🔀" color="#f59e0b"
            cats={globalReorderBase} blockedKeys={blockedKeys} onToggle={toggle} onBulk={bulkAction}
            saving={saving} search={search} mode={mode}
            onReorder={setPendingGlobal} pendingOrder={pendingGlobal} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 14, alignItems: 'start' }}>
            <CategoryPanel title="Premium4K" icon="🔵" color="#3b82f6"
              cats={mode === 'reorder' ? relayReorderBase : relayBase} blockedKeys={blockedKeys} onToggle={toggle} onBulk={bulkAction}
              saving={saving} search={search} mode={mode}
              onReorder={setPendingRelay} pendingOrder={pendingRelay} />
            <CategoryPanel title="iGetTV" icon="🟢" color="#22c55e"
              cats={mode === 'reorder' ? igettReorderBase : igettBase} blockedKeys={blockedKeys} onToggle={toggle} onBulk={bulkAction}
              saving={saving} search={search} mode={mode}
              onReorder={setPendingXtream} pendingOrder={pendingXtream} />
            <CategoryPanel title="DXServer" icon="🟣" color="#a855f7"
              cats={mode === 'reorder' ? dxserverReorderBase : dxserverBase} blockedKeys={blockedKeys} onToggle={toggle} onBulk={bulkAction}
              saving={saving} search={search} mode={mode}
              onReorder={setPendingDxserver} pendingOrder={pendingDxserver} />
            <CategoryPanel title="Premier Services" icon="🟠" color="#f97316"
              cats={premierServicesBase} blockedKeys={blockedKeys} onToggle={toggle} onBulk={bulkAction}
              saving={saving} search={search} mode={mode} />
            <CategoryPanel title="StreamVision" icon="🔴" color="#ec4899"
              cats={dndnsBase} blockedKeys={blockedKeys} onToggle={toggle} onBulk={bulkAction}
              saving={saving} search={search} mode={mode} />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
